'use client';

import { useEffect, useRef } from 'react';
import { trackEvent } from '@/lib/amplitude';
import { usePendingApprovals } from '@/lib/comix/use-pending-approvals';
import PendingApprovalCard from './PendingApprovalCard';

/**
 * Home-page ( /comix ) banner prompting the user to approve printed comic books
 * they bought in the last 48h so shipping can start. Sits in the normal document
 * flow at the very top of the page content, so it pushes the hero down rather than
 * overlapping it. Renders nothing (no layout shift) when there's nothing to approve.
 */
export default function PendingApprovalBanner() {
  const { books, loading } = usePendingApprovals();
  const shownRef = useRef(false);

  useEffect(() => {
    if (books.length > 0 && !shownRef.current) {
      shownRef.current = true;
      trackEvent('Home - Approval Banner Shown', { count: books.length });
    }
  }, [books.length]);

  if (loading || books.length === 0) return null;

  return (
    // min-h-[75dvh] is the tinted backdrop itself, not the card inside it — the card
    // keeps its own compact size (see PendingApprovalCard) and sits vertically
    // centered in this tall band, on every breakpoint (not just mobile).
    <section
      className="flex w-full min-h-[75dvh] items-center"
      style={{ background: 'linear-gradient(180deg, #E9F2FC 0%, #F2F7FD 100%)' }}
    >
      {/* hp-container matches the rest of the homepage's left/right margins, so this
          lines up with "Make your own comic adventure" etc. below it. No extra
          max-width here — the card spans the full container, same as HeroSection and
          StoryBanner do, rather than sitting as a small centered island. */}
      <div className="hp-container w-full py-5 dt:py-8">
        <PendingApprovalCard books={books} source="home-banner" />
      </div>
    </section>
  );
}
